import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Clock } from "lucide-react";

/**
 * AdminHelse – live helsestatus for alle eksterne tjenester platformen avhenger av.
 * Sjekker MongoDB, object storage, NOWPayments, Cloudflare CSAM, NudeNet, Turnstile,
 * web push, GeoLite2-DB, ip-api.com og e-post.
 */

const STATUS_META = {
  ok: {
    label: "OK",
    color: "var(--sage)",
    Icon: CheckCircle2,
    bg: "rgba(127, 168, 142, 0.08)",
    border: "rgba(127, 168, 142, 0.35)",
  },
  degraded: {
    label: "Treig",
    color: "var(--gold)",
    Icon: AlertTriangle,
    bg: "rgba(212, 165, 116, 0.08)",
    border: "rgba(212, 165, 116, 0.4)",
  },
  down: {
    label: "Nede",
    color: "var(--magenta)",
    Icon: XCircle,
    bg: "rgba(209, 26, 91, 0.1)",
    border: "rgba(209, 26, 91, 0.5)",
  },
  not_configured: {
    label: "Ikke aktiv",
    color: "var(--cream-faint)",
    Icon: MinusCircle,
    bg: "rgba(94, 87, 78, 0.08)",
    border: "rgba(94, 87, 78, 0.3)",
  },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.not_configured;
  const Icon = meta.Icon;
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1 rounded-full border font-meta text-[10px]"
      style={{ borderColor: meta.border, backgroundColor: meta.bg, color: meta.color }}
      data-testid={`health-status-${status}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </div>
  );
};

const ServiceRow = ({ service }) => {
  const meta = STATUS_META[service.status] || STATUS_META.not_configured;
  return (
    <div
      className="grid grid-cols-12 gap-4 items-center py-4 px-5 border-l-2 transition-colors hover:bg-white/[0.02]"
      style={{ borderLeftColor: meta.color }}
      data-testid={`health-service-${service.key}`}
    >
      <div className="col-span-12 sm:col-span-4">
        <div className="font-fraunces italic text-lg text-[var(--cream)]">{service.label}</div>
        <div className="font-manrope text-xs text-[var(--cream-faint)] mt-1 leading-relaxed">
          {service.description}
        </div>
      </div>
      <div className="col-span-12 sm:col-span-5">
        <div className="font-manrope text-sm text-[var(--cream-soft)]">{service.message}</div>
        {service.meta && Object.keys(service.meta).length > 0 && (
          <div className="font-manrope text-[11px] text-[var(--cream-faint)] mt-1.5">
            {Object.entries(service.meta).map(([k, v]) => (
              <span key={k} className="mr-3">
                <span className="text-[var(--cream-faint)]">{k}:</span>{" "}
                <span className="text-[var(--cream-soft)]">{String(v)}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-6 sm:col-span-2 text-right">
        {service.latency_ms != null && (
          <div className="font-meta text-[11px] text-[var(--cream-faint)] flex items-center justify-end gap-1">
            <Clock className="h-3 w-3" />
            {service.latency_ms} ms
          </div>
        )}
      </div>
      <div className="col-span-6 sm:col-span-1 text-right">
        <StatusPill status={service.status} />
      </div>
    </div>
  );
};

export default function AdminHelse() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchHealth = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    setError("");
    try {
      const { data: payload } = await api.get("/admin/health");
      setData(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || "Klarte ikke å hente helsestatus.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Auth-guard
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "owner"].includes(user.role)) { navigate("/"); return; }
    fetchHealth();
  }, [user, authLoading, navigate, fetchHealth]);

  // Auto-refresh hvert 30. sekund hvis aktivert
  useEffect(() => {
    if (!autoRefresh) return;
    const i = setInterval(() => fetchHealth(true), 30000);
    return () => clearInterval(i);
  }, [autoRefresh, fetchHealth]);

  if (authLoading || (loading && !data)) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-16">
          <div className="font-meta text-[var(--cream-soft)]">Henter helsestatus …</div>
        </div>
      </Layout>
    );
  }

  const overallMeta = STATUS_META[data?.overall || "not_configured"];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-10" data-testid="admin-helse-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="font-meta text-[var(--magenta)] mb-3">ADMIN · HELSESTATUS</div>
            <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-none">
              System­helse.
            </h1>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mt-3 max-w-2xl">
              Live oversikt over eksterne tjenester. Sjekk denne siden hver morgen i lansering­en
              for å fange opp problemer før brukerne gjør det.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 font-manrope text-xs text-[var(--cream-soft)] cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="accent-[var(--magenta)]"
                data-testid="health-auto-refresh"
              />
              Auto-oppdater (30 s)
            </label>
            <button
              onClick={() => fetchHealth()}
              disabled={refreshing}
              className="btn-magenta inline-flex items-center gap-2 px-4 py-2 text-xs"
              data-testid="health-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Oppdater
            </button>
          </div>
        </div>

        {error && (
          <div className="border border-[var(--magenta)]/40 bg-[var(--magenta)]/10 text-[var(--magenta)] px-4 py-3 mb-6 font-manrope text-sm" data-testid="health-error">
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Overordnet status */}
            <div
              className="border rounded-sm p-6 mb-8 flex items-center justify-between flex-wrap gap-4"
              style={{
                borderColor: overallMeta.border,
                backgroundColor: overallMeta.bg,
              }}
              data-testid="health-overall"
            >
              <div className="flex items-center gap-4">
                <overallMeta.Icon className="h-10 w-10" style={{ color: overallMeta.color }} />
                <div>
                  <div className="font-meta text-[var(--cream-soft)] mb-1">Status</div>
                  <div className="font-fraunces italic text-3xl" style={{ color: overallMeta.color }}>
                    {data.overall === "ok" && "Alt fungerer."}
                    {data.overall === "degraded" && "Noen tjenester er trege."}
                    {data.overall === "down" && "Kritiske tjenester er nede."}
                  </div>
                </div>
              </div>
              <div className="flex gap-6">
                {["ok", "degraded", "down", "not_configured"].map((k) => {
                  const m = STATUS_META[k];
                  return (
                    <div key={k} className="text-center">
                      <div className="font-fraunces italic text-2xl" style={{ color: m.color }}>
                        {data.counts[k] || 0}
                      </div>
                      <div className="font-meta text-[9px] text-[var(--cream-faint)] mt-1">
                        {m.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sist sjekket */}
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-4 text-right">
              SIST SJEKKET: {new Date(data.checked_at).toLocaleString("nb-NO")}
            </div>

            {/* Tjenester-liste */}
            <div className="border border-white/10 bg-[var(--noir-elev)] divide-y divide-white/[0.06]" data-testid="health-services-list">
              {data.services.map((s) => (
                <ServiceRow key={s.key} service={s} />
              ))}
            </div>

            {/* Forklaring */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-manrope text-[var(--cream-faint)] leading-relaxed">
              <div>
                <div className="font-meta text-[var(--cream-soft)] mb-2">OM STATUSENE</div>
                <p><span style={{ color: STATUS_META.ok.color }}>OK</span> – tjenesten svarer som forventet.</p>
                <p><span style={{ color: STATUS_META.degraded.color }}>Treig</span> – konfigurert, men responsen er saktere enn forventet.</p>
                <p><span style={{ color: STATUS_META.down.color }}>Nede</span> – konfigurert, men svarer ikke.</p>
                <p><span style={{ color: STATUS_META.not_configured.color }}>Ikke aktiv</span> – ikke aktivert. Ikke en feil.</p>
              </div>
              <div>
                <div className="font-meta text-[var(--cream-soft)] mb-2">SJEKKE-RYTME</div>
                <p>Anbefalt: åpne denne siden hver morgen i lanserings­uka.</p>
                <p>Aktiver auto-oppdater hvis du har siden oppe under en lansering, demo, eller etter en deploy.</p>
                <p>Helse-endepunktet er rate­vennlig – ingen kvoter brennes av denne siden alene.</p>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
